import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownContentProps {
  children: string;
  className?: string;
}

// Render markdown with readable typography. No external plugin required —
// all styling is inlined via className on each element type.
export function MarkdownContent({ children, className = '' }: MarkdownContentProps) {
  return (
    <div className={`text-sm leading-relaxed text-foreground ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children: c }) => (
            <h1 className="text-xl font-bold mt-0 mb-3 pb-2 border-b border-border leading-tight">{c}</h1>
          ),
          h2: ({ children: c }) => (
            <h2 className="text-base font-semibold mt-5 mb-2 leading-snug">{c}</h2>
          ),
          h3: ({ children: c }) => (
            <h3 className="text-sm font-semibold mt-4 mb-1.5 leading-snug">{c}</h3>
          ),
          h4: ({ children: c }) => (
            <h4 className="text-sm font-medium mt-3 mb-1 leading-snug text-muted-foreground">{c}</h4>
          ),
          p: ({ children: c }) => (
            <p className="mt-0 mb-3 last:mb-0">{c}</p>
          ),
          ul: ({ children: c }) => (
            <ul className="list-disc list-outside pl-5 mb-3 space-y-0.5">{c}</ul>
          ),
          ol: ({ children: c }) => (
            <ol className="list-decimal list-outside pl-5 mb-3 space-y-0.5">{c}</ol>
          ),
          li: ({ children: c }) => (
            <li className="leading-relaxed">{c}</li>
          ),
          blockquote: ({ children: c }) => (
            <blockquote className="border-l-4 border-border pl-4 my-3 text-muted-foreground italic">{c}</blockquote>
          ),
          code: ({ className: cls, children: c, ...props }) => {
            const isBlock = cls?.includes('language-');
            if (isBlock) {
              return (
                <pre className="bg-muted rounded-md p-3 overflow-x-auto my-3 text-xs leading-relaxed font-mono">
                  <code className={cls} {...props}>{c}</code>
                </pre>
              );
            }
            return (
              <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono" {...props}>{c}</code>
            );
          },
          pre: ({ children: c }) => <>{c}</>,
          a: ({ href, children: c }) => (
            <a href={href} className="text-primary underline underline-offset-2 hover:opacity-80" target="_blank" rel="noopener noreferrer">{c}</a>
          ),
          strong: ({ children: c }) => <strong className="font-semibold">{c}</strong>,
          em: ({ children: c }) => <em className="italic">{c}</em>,
          hr: () => <hr className="my-4 border-border" />,
          table: ({ children: c }) => (
            <div className="overflow-x-auto my-3">
              <table className="w-full text-xs border-collapse">{c}</table>
            </div>
          ),
          thead: ({ children: c }) => <thead className="bg-muted">{c}</thead>,
          tr: ({ children: c }) => <tr className="border-b border-border">{c}</tr>,
          th: ({ children: c }) => <th className="text-left px-3 py-2 font-medium text-foreground">{c}</th>,
          td: ({ children: c }) => <td className="px-3 py-2 text-muted-foreground">{c}</td>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
